import type {
  AnovaStats,
  OneWayAnovaResults,
} from '@/components/Studies/Randomization/Steps/Randomize/Statistics.model';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useMemo } from 'react';

interface OneWayAnovaTableProps {
  oneWayAnova: OneWayAnovaResults;
  closeModal: () => void;
}

type TabulatedAnovaStats = AnovaStats & { pvalue?: number; source: string };

const OneWayAnovaTable: FC<OneWayAnovaTableProps> = ({ oneWayAnova, closeModal }) => {
  const tableOrder: Record<string, number> = { treatment: 0, error: 1, total: 2 };
  const tabulatedAnovaData = Object.entries(oneWayAnova.anovaTable).reduce<Array<TabulatedAnovaStats>>(
    // Massaging custom presentation order
    (acc, [key, value]) => {
      acc[tableOrder[key]] = {
        ...value,
        source: key,
        ...(key === 'treatment' ? { pvalue: oneWayAnova.pvalue } : {}),
      };

      return acc;
    },
    []
  );

  const columns = useMemo<Array<TanStackColumnDef<TabulatedAnovaStats>>>(() => {
    return [
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Source',
        meta: { style: { backgroundColor: '#f6f8fa', fontWeight: 500 } },
        enableSorting: false,
      },
      {
        id: 'df',
        accessorKey: 'degreesOfFreedom',
        header: 'Degrees of Freedom',
        enableSorting: false,
      },
      {
        id: 'ss',
        accessorKey: 'sumOfSquares',
        cell: ({ row }: { row: { original: TabulatedAnovaStats } }) => row.original.sumOfSquares?.toFixed(4),
        header: 'Sum of Squares',
        enableSorting: false,
      },
      {
        id: 'ms',
        accessorKey: 'meanSquare',
        cell: ({ row }: { row: { original: TabulatedAnovaStats } }) => row.original.meanSquare?.toFixed(4),
        header: 'Mean Square',
        enableSorting: false,
      },
      {
        id: 'fDist',
        accessorKey: 'fdist',
        cell: ({ row }: { row: { original: TabulatedAnovaStats } }) => row.original.fdist?.toFixed(4),
        header: 'F Stat',
        enableSorting: false,
      },
      {
        id: 'pvalue',
        accessorKey: 'pvalue',
        cell: ({ row }: { row: { original: TabulatedAnovaStats } }) => row.original.pvalue?.toFixed(4),
        header: 'P Value',
        enableSorting: false,
      },
    ];
  }, [oneWayAnova]);

  return (
    <ModalContainer className="pa3">
      <ModalHeader title={`Anova Table`} closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <Table columns={columns} data={tabulatedAnovaData} />
    </ModalContainer>
  );
};

export default OneWayAnovaTable;
